import { Controller } from '@hotwired/stimulus'
import {
  Tabulator,
  FormatModule,
  FilterModule,
  SortModule,
  GroupRowsModule,
  SelectRangeModule,
  InteractionModule,
  ResizeColumnsModule,
  MoveColumnsModule,
  FrozenColumnsModule,
  KeybindingsModule,
  ResizeTableModule
} from 'tabulator-tables'
import { readState, matchesFilter, formatValue, csvFor } from '../reporting/state'
import RangeSelection from '../reporting/range_selection'
import { xlsxFor } from '../reporting/xlsx'

Tabulator.registerModule([
  FormatModule,
  FilterModule,
  SortModule,
  GroupRowsModule,
  SelectRangeModule,
  InteractionModule,
  ResizeColumnsModule,
  MoveColumnsModule,
  FrozenColumnsModule,
  KeybindingsModule,
  ResizeTableModule
])

export default class extends Controller {
  static targets = [
    'reportsTab',
    'columnsTab',
    'reportsPanel',
    'columnsPanel',
    'reportSearch',
    'reportList',
    'columnSearch',
    'columnList',
    'form',
    'formTitle',
    'formError',
    'title',
    'subtitle',
    'actions',
    'groups',
    'empty',
    'emptyTitle',
    'emptyText',
    'loadButton',
    'grid',
    'status',
    'saveButton',
    'saveLabel',
    'loadLabel',
    'exportButton',
    'exportMenu',
    'menu',
    'contextMenu'
  ]

  static values = { reports: Array, selected: Number, url: String }

  connect () {
    this.reports = this.reportsValue
    this.report = null
    this.dirty = false
    this.requests = new Set()
    this.deleting = new Set()
    this.outsideClick = event => {
      if (!this.menuTarget.contains(event.target) && !event.target.closest('[data-column-menu]')) { this.menuTarget.hidden = true }
      if (!this.contextMenuTarget.contains(event.target)) this.contextMenuTarget.hidden = true
      if (!this.exportMenuTarget.contains(event.target) && !event.target.closest('[data-export-trigger]')) this.closeExportMenu()
    }
    this.escapeKey = event => {
      if (event.key === 'Escape') {
        this.menuTarget.hidden = true
        this.contextMenuTarget.hidden = true
        if (!this.exportMenuTarget.hidden) {
          this.closeExportMenu()
          this.exportTrigger?.focus()
        }
      }
    }
    document.addEventListener('click', this.outsideClick)
    document.addEventListener('keydown', this.escapeKey)
    this.renderReports()
    const report = this.reports.find(report => report.id === this.selectedValue)
    if (report) this.selectReport(report)
  }

  disconnect () {
    this.loadRequest?.abort()
    this.requests.forEach(request => request.abort())
    this.table?.destroy()
    this.table = null
    document.removeEventListener('click', this.outsideClick)
    document.removeEventListener('keydown', this.escapeKey)
  }

  node (tag, text, className) {
    const node = document.createElement(tag)
    if (text !== undefined) node.textContent = text
    if (className) node.className = className
    return node
  }

  button (text, label, handler, className) {
    const button = this.node('button', text, className)
    button.type = 'button'
    button.setAttribute('aria-label', label)
    button.addEventListener('click', handler)
    return button
  }

  iconButton (icon, label, handler) {
    const button = this.button(
      '',
      label,
      handler,
      `report-tool-icon-button report-tool-icon-${icon}`
    )
    button.title = label
    button.append(
      this.element
        .querySelector(`template[data-report-tool-icon="${icon}"]`)
        .content.cloneNode(true)
    )
    return button
  }

  showReports () {
    this.setPanel(false)
  }

  showColumns () {
    if (this.table) this.setPanel(true)
  }

  setPanel (columns) {
    this.reportsPanelTarget.hidden = columns
    this.columnsPanelTarget.hidden = !columns
    this.reportsTabTarget.classList.toggle('is-active', !columns)
    this.reportsTabTarget.classList.toggle('selected', !columns)
    this.columnsTabTarget.classList.toggle('is-active', columns)
    this.columnsTabTarget.classList.toggle('selected', columns)
    this.reportsTabTarget.setAttribute('aria-pressed', String(!columns))
    this.columnsTabTarget.setAttribute('aria-pressed', String(columns))
  }

  renderReports () {
    const search = this.reportSearchTarget.value.trim().toLocaleLowerCase()
    const reports = this.reports
      .filter(
        report =>
          report.id !== this.editing?.id &&
          `${report.name} ${report.report_type}`.toLocaleLowerCase().includes(search)
      )
      .sort((a, b) => a.name.localeCompare(b.name))
    this.reportListTarget.replaceChildren(...reports.map(report => this.reportCard(report)))
    if (!reports.length && !this.editing) {
      this.reportListTarget.append(
        this.node(
          'p',
          search ? 'No matching reports.' : 'No reports yet. Create one to get started.'
        )
      )
    }
  }

  reportCard (report) {
    const card = this.node(
      'div',
      undefined,
      `report-tool-card mct-sensor${this.report?.id === report.id ? ' selected is-active' : ''}`
    )
    const name = this.button(
      '',
      `Open ${report.name}`,
      () => this.selectReport(report),
      'report-tool-card-name'
    )
    name.append(this.node('strong', report.name), this.node('small', report.report_type))
    card.append(
      name,
      this.iconButton('edit', `Edit ${report.name}`, () => this.editForm(report)),
      this.iconButton('remove', `Delete ${report.name}`, () => this.deleteReport(report))
    )
    return card
  }

  selectReport (report) {
    if (this.dirty && !window.confirm('Discard unsaved changes to this view?')) return
    this.closeExportMenu()
    this.loadRequest?.abort()
    this.table?.destroy()
    this.table = null
    this.report = report
    this.dirty = false
    this.columnsTabTarget.disabled = true
    this.actionsTarget.hidden = true
    this.groupsTarget.hidden = true
    this.gridTarget.hidden = true
    this.emptyTarget.hidden = false
    this.loadButtonTarget.hidden = false
    this.loadButtonTarget.disabled = false
    this.loadLabelTarget.textContent = 'Load data'
    this.titleTarget.textContent = report.name
    this.subtitleTarget.textContent = `${report.report_type[0].toUpperCase() + report.report_type.slice(1)} report`
    this.emptyTitleTarget.textContent = report.name
    this.emptyTextTarget.textContent =
      'Load the latest data, then choose your columns and refine your view.'
    this.statusTarget.textContent = 'Data has not been loaded.'
    this.menuTarget.hidden = true
    this.contextMenuTarget.hidden = true
    this.showReports()
    this.renderReports()
  }

  createForm () {
    this.openForm()
  }

  editForm (report) {
    this.openForm(report)
  }

  openForm (report) {
    if (this.formSaving) return
    this.editing = report
    this.formTarget.hidden = false
    this.formTarget.elements.name.value = report?.name || ''
    this.formTarget.elements.report_type.value = report?.report_type || 'event'
    this.formTitleTarget.textContent = report ? 'Edit report' : 'Create report'
    this.formErrorTarget.textContent = ''
    this.renderReports()
    this.formTarget.elements.name.focus()
  }

  closeForm () {
    this.formTarget.hidden = true
    this.editing = null
    this.renderReports()
  }

  async request (url, method, body, signal) {
    const request = new AbortController()
    this.requests.add(request)
    try {
      const response = await fetch(url, {
        method,
        signal: signal || request.signal,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        },
        body: body ? JSON.stringify(body) : undefined
      })
      if (response.redirected) { throw new Error('Your session or access changed. Please reload this page.') }
      if (response.status === 204) return null
      const result = await response.json()
      if (!response.ok) {
        throw new Error(
          result.errors?.join('. ') || 'Unable to complete this request. Please try again.'
        )
      }
      return result
    } finally {
      this.requests.delete(request)
    }
  }

  async submitForm (event) {
    event.preventDefault()
    const editing = this.editing
    if (
      editing?.id === this.report?.id &&
      this.dirty &&
      !window.confirm('Discard unsaved view changes before editing this report?')
    ) { return }
    const controls = Array.from(this.formTarget.elements)
    this.formSaving = true
    controls.forEach(control => {
      control.disabled = true
    })
    this.formErrorTarget.textContent = ''
    try {
      const report = await this.request(
        editing ? `${this.urlValue}/${editing.id}` : this.urlValue,
        editing ? 'PATCH' : 'POST',
        {
          report: {
            name: this.formTarget.elements.name.value.trim(),
            report_type: this.formTarget.elements.report_type.value
          }
        }
      )
      this.reports = this.reports.filter(item => item.id !== report.id).concat(report)
      if (!editing || this.report?.id === report.id) {
        this.dirty = false
        this.selectReport(report)
      }
      this.closeForm()
      this.renderReports()
    } catch (error) {
      if (error.name !== 'AbortError') this.formErrorTarget.textContent = error.message
    } finally {
      this.formSaving = false
      controls.forEach(control => {
        control.disabled = false
      })
    }
  }

  async deleteReport (report) {
    if (this.deleting.has(report.id) || !window.confirm(`Delete “${report.name}”?`)) return
    this.deleting.add(report.id)
    try {
      await this.request(`${this.urlValue}/${report.id}`, 'DELETE')
      this.reports = this.reports.filter(item => item.id !== report.id)
      if (this.editing?.id === report.id) this.closeForm()
      if (this.report?.id === report.id) {
        this.loadRequest?.abort()
        this.table?.destroy()
        this.table = null
        this.report = null
        this.dirty = false
        this.actionsTarget.hidden = true
        this.groupsTarget.hidden = true
        this.gridTarget.hidden = true
        this.columnsTabTarget.disabled = true
        this.emptyTarget.hidden = false
        this.loadButtonTarget.hidden = true
        this.titleTarget.textContent = 'Reporting'
        this.subtitleTarget.textContent = 'Select a report to get started.'
        this.emptyTitleTarget.textContent = 'Your reports, in one workspace'
        this.emptyTextTarget.textContent = 'Select a report on the left, or create a new one.'
        this.showReports()
      }
      this.renderReports()
      this.statusTarget.textContent = 'Report deleted.'
    } catch (error) {
      if (error.name !== 'AbortError') this.statusTarget.textContent = error.message
    } finally {
      this.deleting.delete(report.id)
    }
  }

  async loadData () {
    const report = this.report
    this.loadRequest?.abort()
    const request = new AbortController()
    this.loadRequest = request
    this.loadButtonTarget.disabled = true
    this.loadLabelTarget.textContent = 'Loading…'
    this.statusTarget.textContent = 'Loading report data…'
    try {
      const data = await this.request(
        `${this.urlValue}/${report.id}/data`,
        'GET',
        null,
        request.signal
      )
      if (request.signal.aborted || this.report !== report) return
      this.columns = data.columns
      this.defaults = data.groups
      this.state = readState(report.initial_state, data.groups)
      const known = field => this.columns.some(column => column.field === field)
      this.state.groups = this.state.groups.filter(known)
      this.state.hidden = this.state.hidden.filter(known)
      if (this.state.hidden.length === this.columns.length) { this.state.hidden = this.state.hidden.filter(field => field !== this.columns[0].field) }
      this.state.sort = this.state.sort.filter(sort => known(sort.column))
      this.state.filters = Object.fromEntries(
        Object.entries(this.state.filters).filter(([field]) => known(field))
      )
      this.emptyTarget.hidden = true
      this.gridTarget.hidden = false
      this.actionsTarget.hidden = false
      this.columnsTabTarget.disabled = false
      this.exportButtonTarget.disabled = true
      this.saveLabelTarget.textContent = 'Save view'
      this.rows = data.rows
      this.createTable()
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.statusTarget.textContent = error.message
        this.emptyTextTarget.textContent = error.message
      }
    } finally {
      if (this.report === report && this.loadRequest === request) {
        this.loadButtonTarget.disabled = false
        this.loadLabelTarget.textContent = 'Load data'
      }
    }
  }

  createTable () {
    const ordered = this.columns.slice().sort((a, b) => {
      const position = column => {
        const index = this.state.order.indexOf(column.field)
        return index === -1 ? this.state.order.length + this.columns.indexOf(column) : index
      }
      return position(a) - position(b)
    })
    this.table = new Tabulator(this.gridTarget, {
      data: this.rows,
      index: '_row_id',
      height: '100%',
      layout: 'fitData',
      movableColumns: true,
      placeholder: 'No rows match this report or its filters.',
      selectableRange: 1,
      selectableRangeRows: true,
      selectableRangeColumns: true,
      rowHeader: {
        formatter: 'rownum',
        width: 48,
        hozAlign: 'center',
        headerSort: false,
        resizable: false,
        frozen: true
      },
      columnDefaults: { headerSort: false, minWidth: 110, resizable: 'header' },
      columns: ordered.map(column => ({
        field: column.field,
        title: column.title,
        titleFormatter: () => this.node('span', column.title),
        width: this.state.widths[column.field] || 170,
        visible: !this.state.hidden.includes(column.field),
        sorter: column.type === 'text' ? 'string' : 'number',
        formatter: cell => this.node('span', formatValue(cell.getValue(), column)),
        hozAlign: column.type === 'number' ? 'right' : 'left'
      })),
      groupStartOpen: true,
      groupHeader: (value, count) => this.node('span', `${value ?? '(Blank)'} · ${count} rows`)
    })
    this.table.on('tableBuilt', () => {
      this.rangeSelection = new RangeSelection(this.table, this.gridTarget)
      this.applyView()
      this.renderColumns()
      this.showColumns()
    })
    this.table.on('rangeChanged', () => this.updateSelection())
    this.table.on('rangeAdded', () => this.updateSelection())
    this.table.on('rangeRemoved', () => this.updateSelection())
    this.table.on('cellMouseUp', () => this.updateSelection())
    this.table.on('dataFiltered', () => this.updateStatus())
    this.table.on('columnMoved', () => {
      this.state.order = this.table
        .getColumns()
        .map(col => col.getField())
        .filter(Boolean)
      this.changed()
      this.renderColumns()
    })
    this.table.on('columnResized', column => {
      if (column.getField()) {
        this.state.widths[column.getField()] = column.getWidth()
        this.changed()
      }
    })
    this.table.on('cellContext', (event, cell) => this.openContext(event, cell))
    this.table.on('headerContext', event => this.openContext(event))
    this.table.on('rowContext', event => this.openContext(event))
  }

  applyView () {
    this.table.setGroupBy(this.state.groups.length ? this.state.groups.slice() : false)
    this.table.setSort(this.state.sort.map(sort => ({ ...sort })).reverse())
    this.table.setFilter(row =>
      Object.entries(this.state.filters).every(([field, filter]) =>
        matchesFilter(row[field], filter)
      )
    )
    this.renderGroups()
    this.updateStatus()
  }

  updateStatus () {
    if (!this.table) return
    const rows = this.table.getDataCount('active')
    this.statusTarget.textContent = `${rows.toLocaleString()} of ${this.rows.length.toLocaleString()} rows${this.dirty ? ' · Unsaved view' : ''}`
  }

  changed () {
    this.dirty = true
    this.saveLabelTarget.textContent = 'Save view •'
    this.updateStatus()
  }

  renderColumns () {
    if (!this.table) return
    const search = this.columnSearchTarget.value.trim().toLocaleLowerCase()
    this.columnListTarget.replaceChildren(
      ...this.table
        .getColumns()
        .filter(col => col.getField())
        .map(col => this.columns.find(column => column.field === col.getField()))
        .filter(column => column.title.toLocaleLowerCase().includes(search))
        .map(column => this.columnCard(column))
    )
    if (!this.columnListTarget.children.length) { this.columnListTarget.append(this.node('p', 'No matching columns.')) }
  }

  columnCard (column) {
    const card = this.node('div', undefined, 'report-tool-card mct-sensor')
    const check = this.node('input')
    check.type = 'checkbox'
    check.checked = this.table.getColumn(column.field).isVisible()
    check.setAttribute('aria-label', `Show ${column.title}`)
    check.addEventListener('change', () => {
      if (
        !check.checked &&
        this.table.getColumns().filter(col => col.getField() && col.isVisible()).length === 1
      ) {
        check.checked = true
        this.statusTarget.textContent = 'Keep at least one column visible.'
        return
      }
      this.table.getRanges().forEach(range => range.remove())
      this.table.getColumn(column.field).toggle()
      this.state.hidden = this.table
        .getColumns()
        .filter(col => col.getField() && !col.isVisible())
        .map(col => col.getField())
      this.changed()
    })
    const label = this.node('div', undefined, 'report-tool-column-name')
    label.append(this.node('span', column.title))
    const badges = this.node('div', undefined, 'report-tool-badges')
    if (this.state.groups.includes(column.field)) {
      badges.append(
        this.node(
          'span',
          `Group ${this.state.groups.indexOf(column.field) + 1}`,
          'report-tool-badge'
        )
      )
    }
    const sort = this.state.sort.find(sort => sort.column === column.field)
    if (sort) {
      badges.append(
        this.node('span', sort.dir === 'asc' ? 'Ascending ↑' : 'Descending ↓', 'report-tool-badge')
      )
    }
    if (this.state.filters[column.field]) { badges.append(this.node('span', 'Filtered', 'report-tool-badge')) }
    label.append(badges)
    const menu = this.iconButton('more', `Options for ${column.title}`, event =>
      this.openColumnMenu(column, event.currentTarget)
    )
    menu.dataset.columnMenu = column.field
    card.append(check, label, menu)
    return card
  }

  renderGroups () {
    this.groupsTarget.hidden = !this.state.groups.length
    this.groupsTarget.replaceChildren(this.node('span', 'Grouped by'))
    this.state.groups.forEach(field =>
      this.groupsTarget.append(
        this.button(
          `${this.columns.find(col => col.field === field).title} ×`,
          `Remove grouping for ${field}`,
          () => this.toggleGroup(field)
        )
      )
    )
  }

  toggleGroup (field) {
    this.state.groups = this.state.groups.includes(field)
      ? this.state.groups.filter(group => group !== field)
      : this.state.groups.concat(field)
    this.applyView()
    this.changed()
    this.renderColumns()
    this.menuTarget.hidden = true
  }

  sortColumn (field, dir) {
    this.state.sort = this.state.sort.filter(sort => sort.column !== field)
    if (dir) this.state.sort.unshift({ column: field, dir })
    this.table.setSort(this.state.sort.map(sort => ({ ...sort })).reverse())
    this.changed()
    this.renderColumns()
    this.menuTarget.hidden = true
  }

  positionMenu (menu, left, top) {
    menu.hidden = false
    menu.style.left = `${Math.max(8, Math.min(left, window.innerWidth - menu.offsetWidth - 8))}px`
    menu.style.top = `${Math.max(8, Math.min(top, window.innerHeight - menu.offsetHeight - 8))}px`
  }

  openColumnMenu (column, button) {
    const menu = this.menuTarget
    menu.replaceChildren(this.node('h3', column.title))
    const grouped = this.state.groups.includes(column.field)
    menu.append(
      this.button(grouped ? 'Remove grouping' : 'Group by this column', 'Toggle grouping', () =>
        this.toggleGroup(column.field)
      )
    )
    const sorts = this.node('div', undefined, 'report-tool-menu-actions')
    sorts.append(
      this.button('Ascending ↑', 'Sort ascending', () => this.sortColumn(column.field, 'asc')),
      this.button('Descending ↓', 'Sort descending', () => this.sortColumn(column.field, 'desc')),
      this.button('Clear sort', 'Clear sort', () => this.sortColumn(column.field))
    )
    menu.append(sorts)
    menu.append(this.filterForm(column))
    const rect = button.getBoundingClientRect()
    this.positionMenu(menu, rect.right + 8, rect.top)
    menu.querySelector('input').focus()
  }

  filterForm (column) {
    const form = this.node('form')
    form.className = 'report-tool-form'
    const filter = this.state.filters[column.field]
    const label = this.node('label', 'Filter values')
    const operation = this.node('select')
    operation.setAttribute('aria-label', 'Filter condition')
    const options =
      column.type === 'text'
        ? [
            'contains',
            'notContains',
            'equals',
            'notEqual',
            'startsWith',
            'endsWith',
            'blank',
            'notBlank'
          ]
        : [
            'equals',
            'notEqual',
            'greaterThan',
            'greaterThanOrEqual',
            'lessThan',
            'lessThanOrEqual',
            'inRange',
            'blank',
            'notBlank'
          ]
    const names = {
      contains: 'Contains',
      notContains: 'Does not contain',
      equals: 'Equals',
      notEqual: 'Does not equal',
      startsWith: 'Starts with',
      endsWith: 'Ends with',
      greaterThan: 'Greater than',
      greaterThanOrEqual: 'At least',
      lessThan: 'Less than',
      lessThanOrEqual: 'At most',
      inRange: 'Between',
      blank: 'Is blank',
      notBlank: 'Is not blank'
    }
    options.forEach(type => {
      const option = this.node('option', names[type])
      option.value = type
      operation.append(option)
    })
    operation.value = options.includes(filter?.type) ? filter.type : options[0]
    label.append(operation)
    const input = this.node('input')
    input.type = column.type === 'date' ? 'date' : column.type === 'number' ? 'number' : 'search'
    if (column.type === 'number') input.step = 'any'
    input.placeholder = 'Search values…'
    input.setAttribute('aria-label', `Filter ${column.title}`)
    input.value =
      column.type === 'date' ? (filter?.dateFrom || '').slice(0, 10) : (filter?.filter ?? '')
    const end = input.cloneNode()
    end.setAttribute('aria-label', 'Range end')
    end.value =
      column.type === 'date' ? (filter?.dateTo || '').slice(0, 10) : (filter?.filterTo ?? '')
    const configure = () => {
      input.hidden = ['blank', 'notBlank'].includes(operation.value)
      input.required = !input.hidden
      end.hidden = operation.value !== 'inRange'
      end.required = !end.hidden
    }
    operation.addEventListener('change', configure)
    configure()
    form.append(label, input, end)
    if (filter?.conditions || filter?.filterType === 'set') {
      form.append(
        this.node(
          'p',
          'This column has a saved compound filter. Applying a new filter replaces it.'
        )
      )
    }
    const apply = this.node('button', 'Apply filter', 'report-tool-primary')
    apply.type = 'submit'
    form.append(
      apply,
      this.button('Clear filter', 'Clear filter', () => {
        delete this.state.filters[column.field]
        this.applyView()
        this.changed()
        this.renderColumns()
        this.menuTarget.hidden = true
      })
    )
    form.addEventListener('submit', event => {
      event.preventDefault()
      if (
        operation.value === 'inRange' &&
        (column.type === 'number'
          ? Number(input.value) > Number(end.value)
          : input.value > end.value)
      ) {
        end.setCustomValidity('The end must be greater than or equal to the start.')
        end.reportValidity()
        end.setCustomValidity('')
        return
      }
      const next = { filterType: column.type, type: operation.value }
      if (column.type === 'date') {
        next.dateFrom = input.value
        next.dateTo = end.value
      } else {
        next.filter = input.value
        next.filterTo = end.value
      }
      this.state.filters[column.field] = next
      this.applyView()
      this.changed()
      this.renderColumns()
      this.menuTarget.hidden = true
    })
    return form
  }

  updateSelection () {
    if (!this.table) return
    this.exportButtonTarget.disabled = !this.table
      .getRanges()
      .some(range => range.getRows().length && range.getColumns().some(col => col.getField()))
  }

  openContext (event, cell) {
    event.preventDefault()
    if (
      cell &&
      !this.table.getRanges().some(range =>
        range
          .getCells()
          .flat()
          .some(
            selected =>
              selected.getRow().getIndex() === cell.getRow().getIndex() &&
              selected.getField() === cell.getField()
          )
      )
    ) { this.table.addRange(cell, cell) }
    this.closeExportMenu()
    this.updateSelection()
    if (this.exportButtonTarget.disabled) return
    this.positionMenu(this.contextMenuTarget, event.clientX, event.clientY)
    this.contextMenuTarget.querySelector('button').focus()
  }

  openExportMenu ({ currentTarget, params: { selection } }) {
    const toggleOff = this.exportTrigger === currentTarget && !this.exportMenuTarget.hidden
    this.closeExportMenu()
    if (toggleOff) return
    this.exportTrigger = currentTarget
    this.exportSelectionMode = selection
    this.menuTarget.hidden = true
    this.contextMenuTarget.hidden = true
    const bounds = currentTarget.getBoundingClientRect()
    this.positionMenu(this.exportMenuTarget, bounds.left, bounds.bottom + 6)
    currentTarget.setAttribute('aria-expanded', 'true')
    this.exportMenuTarget.querySelector('button').focus()
  }

  closeExportMenu () {
    this.exportMenuTarget.hidden = true
    this.exportTrigger?.setAttribute('aria-expanded', 'false')
  }

  exportFromMenu (event) {
    this.closeExportMenu()
    if (this.exportSelectionMode) this.exportSelection(event)
    else this.exportAll(event)
    this.exportTrigger?.focus()
  }

  exportSelection (event) {
    const range = this.table?.getRanges()[0]
    if (!range) {
      this.statusTarget.textContent = 'Select cells, rows or columns first.'
      return
    }
    const columns = range
      .getColumns()
      .filter(col => col.getField() && col.isVisible())
      .map(col => this.columns.find(column => column.field === col.getField()))
    const rows = range.getRows().map(row => row.getData())
    this.download(columns, rows, event?.params?.format || 'csv')
    this.contextMenuTarget.hidden = true
  }

  exportAll (event) {
    const columns = this.table
      .getColumns()
      .filter(col => col.getField() && col.isVisible())
      .map(col => this.columns.find(column => column.field === col.getField()))
    this.download(columns, this.table.getData('active'), event?.params?.format || 'csv')
  }

  async download (columns, rows, format = 'csv') {
    if (!columns.length || !rows.length) {
      this.statusTarget.textContent = 'There is no data to export.'
      return
    }
    const filename = this.report.name.replace(/[^\p{L}\p{N} _-]/gu, '_') || 'report'
    let blob
    try {
      blob = format === 'xlsx'
        ? await xlsxFor(columns, rows)
        : new Blob([csvFor(columns, rows)], { type: 'text/csv;charset=utf-8;' })
    } catch (error) {
      this.statusTarget.textContent = 'Unable to export this report. Please try again.'
      return
    }
    const url = URL.createObjectURL(blob)
    const link = this.node('a')
    link.href = url
    link.download = `${filename}.${format}`
    document.body.append(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
    this.statusTarget.textContent = `Exported ${rows.length} rows and ${columns.length} columns.`
  }

  async saveView () {
    const report = this.report
    const state = structuredClone(this.state)
    this.saveButtonTarget.disabled = true
    try {
      const saved = await this.request(`${this.urlValue}/${report.id}`, 'PATCH', {
        report: { initial_state: { ...report.initial_state, reporting: state } }
      })
      this.reports = this.reports.map(item => (item.id === saved.id ? saved : item))
      if (this.report === report) {
        this.report = saved
        if (JSON.stringify(this.state) === JSON.stringify(state)) {
          this.dirty = false
          this.saveLabelTarget.textContent = 'Save view'
        }
        this.updateStatus()
      }
      this.renderReports()
    } catch (error) {
      if (error.name !== 'AbortError') this.statusTarget.textContent = error.message
    } finally {
      this.saveButtonTarget.disabled = false
    }
  }

  resetView () {
    if (!window.confirm('Reset this report’s column settings and filters?')) return
    this.state = readState({}, this.defaults)
    this.table.destroy()
    this.table = null
    this.createTable()
    this.changed()
  }
}
